import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PhoneMissed, PhoneOutgoing, Clock, Loader2, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
 
const T = {
  en: {
    title: 'Missed Calls', noMissed: 'No missed calls', callBack: 'Call Back',
    calledBack: 'Called Back', refresh: 'Refresh', loading: 'Loading...',
    unknown: 'Unknown Caller', language: 'Language', reason: 'Reason',
    reasons: { new_listing: 'New listing', follow_up: 'Follow-up', speak_advisor: 'Speak to advisor', other: 'Other' } as Record<string, string>,
  },
  fr: {
    title: 'Appels manqués', noMissed: 'Aucun appel manqué', callBack: 'Rappeler',
    calledBack: 'Rappelé', refresh: 'Actualiser', loading: 'Chargement...',
    unknown: 'Appelant inconnu', language: 'Langue', reason: 'Motif',
    reasons: { new_listing: 'Nouvelle inscription', follow_up: 'Suivi de dossier', speak_advisor: 'Parler à un conseiller', other: 'Autre' } as Record<string, string>,
  },
};
 
interface MissedCall {
  id: string;
  from_number: string;
  caller_name: string | null;
  status: string;
  created_at: string;
  metadata: any;
}
 
interface MissedCallsDashboardProps {
  onCallBack?: (phoneNumber: string) => void;
}
 
export function MissedCallsDashboard({ onCallBack }: MissedCallsDashboardProps) {
  const { language } = useLanguage();
  const t = T[language as keyof typeof T] || T.en;
  const dateLocale = language === 'fr' ? fr : enUS;
 
  const [missedCalls, setMissedCalls] = useState<MissedCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [calledBackIds, setCalledBackIds] = useState<Set<string>>(new Set());
 
  // → onSnapshot Firestore — realtime natif, pas de canal à gérer
  useEffect(() => {
    const q = query(
      collection(db, 'incomingCalls'),
      where('status', 'in', ['missed', 'no-answer', 'ringing', 'queued']),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
 
    const unsubscribe = onSnapshot(q, (snap) => {
      const calls: MissedCall[] = snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          from_number: data.fromNumber || data.from_number || '',
          caller_name: data.callerName || data.caller_name || null,
          status: data.status,
          created_at: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          metadata: data.metadata || {},
        };
      });
      setMissedCalls(calls);
      setLoading(false);
    });
 
    return () => unsubscribe();
  }, []);
 
  const handleCallBack = (call: MissedCall) => {
    setCalledBackIds(prev => new Set(prev).add(call.id));
    onCallBack?.(call.from_number);
  };
 
  const getCallReason = (metadata: any): string | null => {
    if (!metadata?.call_reason) return null;
    return t.reasons[metadata.call_reason] || metadata.call_reason;
  };
 
  const getCallLanguage = (metadata: any): string | null => {
    const langMap: Record<string, string> = { en: '🇬🇧 EN', fr: '🇫🇷 FR', es: '🇪🇸 ES' };
    return metadata?.selected_language ? langMap[metadata.selected_language] || metadata.selected_language : null;
  };
 
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">{t.loading}</span>
      </div>
    );
  }
 
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <PhoneMissed className="h-5 w-5 text-destructive" />
          {t.title}
          {missedCalls.length > 0 && <Badge variant="destructive" className="ml-1">{missedCalls.length}</Badge>}
        </h3>
      </div>
 
      {missedCalls.length === 0 ? (
        <Card><CardContent className="py-8 text-center">
          <PhoneMissed className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t.noMissed}</p>
        </CardContent></Card>
      ) : (
        <ScrollArea className="h-[500px]">
          <div className="space-y-2 pr-2">
            {missedCalls.map(call => {
              const wasCalledBack = calledBackIds.has(call.id);
              const reason = getCallReason(call.metadata);
              const callLang = getCallLanguage(call.metadata);
              return (
                <Card key={call.id} className={`transition-colors ${wasCalledBack ? 'opacity-60' : ''}`}>
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-destructive/10 flex items-center justify-center">
                          <PhoneMissed className="h-4 w-4 text-destructive" />
                        </div>
                        <div>
                          <div className="font-medium text-sm">{call.caller_name || call.from_number}</div>
                          {call.caller_name && <div className="text-xs text-muted-foreground">{call.from_number}</div>}
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDistanceToNow(new Date(call.created_at), { addSuffix: true, locale: dateLocale })}
                            </span>
                            {callLang && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{callLang}</Badge>}
                            {reason && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{reason}</Badge>}
                          </div>
                        </div>
                      </div>
                      <div>
                        {wasCalledBack ? (
                          <Badge variant="outline" className="text-emerald-600 border-emerald-500/30">{t.calledBack} ✓</Badge>
                        ) : (
                          <Button size="sm" variant="default" className="gap-1.5" onClick={() => handleCallBack(call)}>
                            <PhoneOutgoing className="h-3.5 w-3.5" />{t.callBack}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
 